import { FC, useMemo } from "react";
import { formatInTimeZone } from "date-fns-tz";

import {
  NOTIFICATION_ACTION_TYPES,
  createNotificationAction,
} from "shared/ui/notifications/notification-generic/base-parts/actions";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import { NotificationBase } from "@/modules/notifications/notification/base";
import {
  DialBlockRequestWaitingNotificationI,
  NotificationI,
} from "@/lib/types/notification";
import { DIAL_BLOCK_ET_ZONE } from "@/modules/dial-blocks/lib/constants";
import { getEtInstant, isValidDayKey } from "@/modules/dial-blocks/lib/week";
import Router from "@/helpers/router";

type NotificationDialBlockRequestWaitingPropsI = NotificationI &
  PropsWithClassNameI;

/** The Calendar view the LGA's card opens (C-12 C's ?view=). */
const REQUESTS_VIEW = "requests";

const plural = (count: number, word: string) =>
  `${count} ${count === 1 ? word : `${word}s`}`;

/** "the week of Oct 26", or "a week" when the week isn't a date. */
const theWeekOf = (weekStart: string | undefined) =>
  weekStart && isValidDayKey(weekStart)
    ? `the week of ${formatInTimeZone(
        getEtInstant(weekStart, 12 * 60),
        DIAL_BLOCK_ET_ZONE,
        "MMM d"
      )}`
    : "a week";

/**
 * The request in one sentence (C12-D235). A number missing from the
 * payload is left out, never shown as 0.
 */
export const describeRequestWaiting = (
  alert: DialBlockRequestWaitingNotificationI | undefined
): string => {
  const who = alert?.caller_name?.trim() || "A caller";
  const where = alert?.workspace_name?.trim()
    ? ` (${alert.workspace_name.trim()})`
    : "";
  const blocks = alert?.blocks;

  if (alert?.kind === "usual") {
    return `${who}${where} asked to change their usual commitment${
      blocks === undefined ? "" : ` to ${plural(blocks, "block")} a week`
    }.`;
  }

  const week = theWeekOf(alert?.week_start);
  const to = blocks === undefined ? "" : ` to ${plural(blocks, "block")}`;

  if (alert?.is_repeat) {
    return `${who}${where} sent another update for ${week}${to}.`;
  }

  return `${who}${where} asked to lower ${week}${to}.`;
};

/**
 * DialBlockRequestWaiting (C-12 wave 2 B, C12-D235): a caller the recipient
 * manages sent a block request. An LGA reviews it in Calendar → Requests;
 * a Pod Lead (who has no Requests view) opens Rep Health on the caller.
 */
export const NotificationDialBlockRequestWaiting: FC<
  NotificationDialBlockRequestWaitingPropsI
> = (props) => {
  const { id, is_seen, created_at, className, dial_block_request_waiting } =
    props;
  const text = useMemo(
    () => describeRequestWaiting(dial_block_request_waiting),
    [dial_block_request_waiting]
  );
  const isPodLead = dial_block_request_waiting?.recipient_role === "pod_lead";

  return (
    <NotificationBase
      id={id}
      isUnread={!is_seen}
      className={className}
      avatar={props}
      description={{ text: <>{text}</>, date: created_at }}
      actions={[
        createNotificationAction(
          NOTIFICATION_ACTION_TYPES.PRIMARY,
          isPodLead ? "Open Rep Health" : "Review request",
          () => {
            window.location.href = isPodLead
              ? Router.manageRepHealth(
                  dial_block_request_waiting?.workspace_id,
                  dial_block_request_waiting?.caller_user_id
                )
              : Router.manageCalendar(
                  dial_block_request_waiting?.workspace_id,
                  REQUESTS_VIEW
                );
          }
        ),
      ]}
    />
  );
};
